import { css } from "@emotion/css";
import { Button, Drawer, ErrorPanel, ExternalLink, LinearProgress } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { ExternalLinkSlugs } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDConstants";
import { createAnnotation } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/createAnnotation";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { CodeWithChrome } from "~/components/Code/CodeWithChrome";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { Select } from "~/components/form";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import { sortDropDownItems } from "~/primitiveComponents/form/Select/Select";
const styles = {
    container: css({
        padding: space[16],
        paddingBottom: space[24],
        display: "flex",
        flexDirection: "column",
        gap: space[16],
        p: {
            margin: 0,
        },
    }),
    filterContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[24],
    }),
};
const GenerateArgoCDAnnotationsDrawerInner = () => {
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const [projectSlug, setProjectSlug] = useState<string | undefined>(undefined);
    const [environmentSlug, setEnvironmentSlug] = useState<string | undefined>(undefined);
    const [tenantSlug, setTenantSlug] = useState<string | undefined>(undefined);
    const { result, isLoading, error } = useInlineStatusQuery(async (repo) => {
        const projectsPromise = repo.Projects.all();
        const environmentsPromise = getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsEnabled, repo);
        const tenantsPromise = repo.Tenants.all();
        return {
            projects: await projectsPromise,
            environments: await environmentsPromise,
            tenants: await tenantsPromise,
        };
    }, [ephemeralEnvironmentsEnabled], "List projects, environments and tenants for annotations drawer");
    //We sort the project items here so when we retrieve the first one to set a default value, we get the same first item as in the select
    //Otherwise you get the first created project, which might be sorted into a different order in the drop down
    const projectItems: Item[] = useMemo(() => sortDropDownItems((result?.projects || []).map((p) => ({
        value: p.Slug,
        text: p.Name,
    }))), [result?.projects]);
    const environmentItems: Item[] = useMemo(() => sortBy(result?.environments || [], (e) => e.SortOrder).map((e) => ({
        value: e.Slug,
        text: e.Name,
    })), [result?.environments]);
    const tenantItems: Item[] = useMemo(() => (result?.tenants || []).map((t) => ({
        value: t.Slug,
        text: t.Name,
    })), [result?.tenants]);
    // Preselect the first project & environment
    useEffect(() => {
        //the value is the slug
        const firstProjectSlug = projectItems?.[0]?.value;
        if (!projectSlug && firstProjectSlug) {
            setProjectSlug(firstProjectSlug);
        }
        //the value is the slug
        const firstEnvironmentSlug = environmentItems?.[0]?.value;
        if (!environmentSlug && firstEnvironmentSlug) {
            setEnvironmentSlug(firstEnvironmentSlug);
        }
    }, [projectItems, environmentItems, projectSlug, environmentSlug]);
    const annotation = useMemo(() => {
        if (!projectSlug || !environmentSlug) {
            return;
        }
        return createAnnotation(projectSlug, environmentSlug, tenantSlug);
    }, [environmentSlug, projectSlug, tenantSlug]);
    const errorInfo = useMemo(() => error && createErrorInfoFromError(error), [error]);
    return (<>
            <LinearProgress show={isLoading} variant={"indeterminate"}/>
            {errorInfo && <ErrorPanel error={errorInfo}/>}
            {!error && (<div className={styles.container}>
                    <p>Add annotations to your Argo CD application YAML with the matching Octopus project, environment and tenant slug.</p>
                    <p>
                        Need help with annotations? <ExternalLink href={ExternalLinkSlugs.AnnotationsDocs} label="Read the docs"/>
                    </p>
                    <div className={styles.filterContainer}>
                        <Select label={"Project"} allowClear={false} items={projectItems} value={projectSlug} onChange={setProjectSlug} disabled={projectItems.length === 0} allowFilter={true} sortItems={false}/>
                        <Select label={"Environment"} allowClear={false} items={environmentItems} value={environmentSlug} onChange={setEnvironmentSlug} disabled={environmentItems.length === 0} allowFilter={true} sortItems={false}/>
                        <Select label={"Tenant (optional)"} allowClear={true} items={tenantItems} value={tenantSlug} onChange={setTenantSlug} disabled={tenantItems.length === 0} allowFilter={true}/>
                    </div>
                    {annotation && (<>
                            <p>Copy the annotations below and add them to your Argo CD application manifest:</p>
                            <CodeWithChrome canCopy={true} canShowFullScreen={false} value={annotation} language={"YAML"}/>
                        </>)}
                </div>)}
        </>);
};
type GenerateArgoCDAnnotationsDrawerProps = {
    open: boolean;
    onClose: () => void;
};
export const GenerateArgoCDAnnotationsDrawer = (props: GenerateArgoCDAnnotationsDrawerProps) => (<Drawer variant={"customActions"} isOpen={props.open} onClose={props.onClose} title={"Generate annotations"} width={"wide"} layout={"basic"} customActions={[<Button importance={"secondary"} onClick={props.onClose} label="Close"/>]}>
        <GenerateArgoCDAnnotationsDrawerInner />
    </Drawer>);
